import type { ProjectContextValue } from '@core/context';

import { useContext } from 'react';

import { ProjectContext } from '@core/context';
import useDashLinkUrl from '@core/hooks/useDashLinkUrl';

interface PlanPageUrlResult {
  isInternal: boolean;
  target?: '_blank';
  url: string;
}

/**
 * Hook that returns the appropriate URL for the manage plans page based on feature flag.
 * When the SuperHub plan management flag is enabled, returns the SuperHub route.
 * Otherwise, returns the Dash route with target="_blank".
 *
 * @returns Object containing url, isInternal flag, and optional target
 *
 * @example
 * ```tsx
 * const planPageUrl = usePlanPageUrl();
 * <Button href={planPageUrl.url} target={planPageUrl.target}>Manage Plan</Button>
 * ```
 */
export default function usePlanPageUrl(): PlanPageUrlResult {
  const { project } = useContext(ProjectContext) as ProjectContextValue;
  const dashLinkUrl = useDashLinkUrl();

  // Check if the SUPERHUB_PLAN_MANAGEMENT flag is enabled
  const useSuperHubPlanManagement = project?.flags?.superHubPlanManagement;

  if (useSuperHubPlanManagement) {
    return {
      url: '#/settings/manage-plans',
      isInternal: true,
      target: undefined,
    };
  }

  return {
    url: dashLinkUrl('plans'),
    isInternal: false,
    target: '_blank',
  };
}
